import type { FloorLayoutModule, PhysicalModule } from '../floor/dynamic-floor.js';
import type { DecorPlacement, TableLayoutOffset } from './layout-types.js';
import { loadLayoutDocument, saveLayoutDocument } from './layout-persistence.js';

interface HistoryEntry {
  modules: PhysicalModule[];
  decor: DecorPlacement[];
  offsets: TableLayoutOffset[];
}

const MAX_HISTORY = 40;

export class TableLayoutEditor {
  private offsets = new Map<string, TableLayoutOffset>();
  private decor: DecorPlacement[] = [];
  private selectedModuleId: string | null = null;
  private decorSeq = 0;
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];

  constructor() {
    const saved = loadLayoutDocument();
    if (saved) {
      for (const o of saved.offsets) this.offsets.set(o.moduleId, o);
      this.decor = saved.decor;
    }
  }

  getSelectedModuleId(): string | null {
    return this.selectedModuleId;
  }

  select(moduleId: string | null): void {
    this.selectedModuleId = moduleId;
  }

  getOffsets(): TableLayoutOffset[] {
    return [...this.offsets.values()];
  }

  getDecor(): DecorPlacement[] {
    return this.decor.map((d) => ({ ...d }));
  }

  clearOffset(moduleId: string): void {
    const cur = this.offsets.get(moduleId);
    if (cur) {
      cur.dx = 0;
      cur.dz = 0;
    }
  }

  ensureFromLayout(layout: FloorLayoutModule[]): void {
    for (const m of layout) {
      if (!this.offsets.has(m.moduleId)) {
        this.offsets.set(m.moduleId, {
          moduleId: m.moduleId,
          logicalTableId: m.logicalTableId,
          dx: 0,
          dz: 0,
          rotationY: 0,
        });
      }
    }
  }

  pushHistory(modules: PhysicalModule[]): void {
    this.undoStack.push({
      modules: modules.map((m) => ({ ...m })),
      decor: this.getDecor(),
      offsets: this.getOffsets().map((o) => ({ ...o })),
    });
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift();
    this.redoStack = [];
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(currentModules: PhysicalModule[]): PhysicalModule[] | null {
    if (!this.undoStack.length) return null;
    this.redoStack.push({
      modules: currentModules.map((m) => ({ ...m })),
      decor: this.getDecor(),
      offsets: this.getOffsets().map((o) => ({ ...o })),
    });
    const prev = this.undoStack.pop()!;
    this.decor = prev.decor.map((d) => ({ ...d }));
    this.offsets = new Map(prev.offsets.map((o) => [o.moduleId, { ...o }]));
    return prev.modules.map((m) => ({ ...m }));
  }

  redo(currentModules: PhysicalModule[]): PhysicalModule[] | null {
    if (!this.redoStack.length) return null;
    this.undoStack.push({
      modules: currentModules.map((m) => ({ ...m })),
      decor: this.getDecor(),
      offsets: this.getOffsets().map((o) => ({ ...o })),
    });
    const next = this.redoStack.pop()!;
    this.decor = next.decor.map((d) => ({ ...d }));
    this.offsets = new Map(next.offsets.map((o) => [o.moduleId, { ...o }]));
    return next.modules.map((m) => ({ ...m }));
  }

  nudgeSelected(dx: number, dz: number): void {
    if (!this.selectedModuleId) return;
    const cur = this.offsets.get(this.selectedModuleId);
    if (!cur) return;
    cur.dx += dx;
    cur.dz += dz;
  }

  setDragDelta(moduleId: string, dx: number, dz: number): void {
    const cur = this.offsets.get(moduleId);
    if (!cur) return;
    cur.dx = dx;
    cur.dz = dz;
  }

  rotateSelected(step = Math.PI / 4): void {
    if (!this.selectedModuleId) return;
    const cur = this.offsets.get(this.selectedModuleId);
    if (!cur) return;
    cur.rotationY += step;
  }

  duplicateDecorFromSelection(layout: FloorLayoutModule[]): void {
    const sel = this.selectedModuleId;
    if (!sel) return;
    const mod = layout.find((m) => m.moduleId === sel);
    const base = mod ? { x: mod.worldX, z: mod.worldZ } : { x: 0, z: 0 };
    const off = this.offsets.get(sel);
    this.decorSeq += 1;
    this.decor.push({
      id: `decor-${this.decorSeq}`,
      kind: 'plant',
      x: base.x + (off?.dx ?? 0) + 1.2,
      z: base.z + (off?.dz ?? 0),
      rotationY: off?.rotationY ?? 0,
    });
  }

  save(): string {
    const doc = saveLayoutDocument(this.getOffsets(), this.decor);
    return doc.savedAt;
  }
}
